import {
	Dialog,
	DialogContent,
	DialogTitle,
	Popover,
	Typography,
} from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import { IoCaretDown, IoClose } from "react-icons/io5";
import CustomStyledTable from "../../../../components/table/CustomStyledTable";
import {
	APP_CONFIG,
	CustomizedTooltip,
	DropdownStyle,
	formatDate,
} from "../../../../helpers/appHelpers";
import { useManageTableWithSearchParams } from "../../../../helpers/useManageTable";
import { PayPeriodDto } from "../../../../models/pay-period.dto";
import {
	BankReportResponseDto,
	EmployeeAnnualTaxDeductionResponseDto,
	PayrollSummaryItemDto,
	PayrollSummaryResponseDto,
	ReportType,
	SalaryAdvanceReportResponseDto,
	SalaryReportResponseDto,
	SavingSchemeReportResponseDto,
	SSFResponseDto,
	TaxReportResponseDto,
} from "../../../../models/payroll.dto";
import { useGetPayPeriod } from "../../../../services/pay-period.service";
import useCompanyStore from "../../../../state-management/useCompanyStore";
import API from "../../../../services/utils/api";
import { useAsyncEffect, useLockFn } from "ahooks";
import {
	useGetBankReport,
	useGetPayrollSummary,
	useGetSalaryAdvanceReport,
	useGetSalaryReport,
	useGetSavingSchemeReport,
	useGetSSFReport,
	useGetTaxReport,
	useGetPayeSummaryReport,
} from "../../../../services/payroll.service";
import {
	BankReportColumns,
	PayrollSummaryColumns,
	SalaryAdvanceReportColumns,
	SalaryReportColumns,
	SavingSchemeColumns,
	SSFReportColumns,
	TaxReportColumns,
} from "../data";
import { PensionTier } from "../../../../models/pension.dto";
import PayrollReportPDF from "../resources/PayrollReportPDF";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { GridColDef } from "@mui/x-data-grid";
import * as XLSX from "xlsx";
import { BsDownload } from "react-icons/bs";
import { FaSortDown } from "react-icons/fa";
import {
	PERMISSIONS,
	useCheckUserPermissions,
} from "../../../../helpers/usePermissionManager";
import {
	salaryReportColumns,
	ssfReportColumns,
	savingSchemeColumns,
	bankReportColumns,
	taxReportColumns,
	payrollSummaryColumns,
	salaryAdvanceReportColumns,
	payeSummaryColumns,
	employeeAnnualTaxDeductionColumns,
} from "../tanstack-columns";
import TanStackEmployeeTable from "../../../../components/table/TanStackEmployeeTable";
import { TableId } from "../../../../models/table-configuration.dto";
import { EmployeeDto, EmployeeOrderBy } from "../../../../models/employee.dto";
import { useGetEmployee } from "../../../../services/employee.service";

type ReportTable = {
	title: string;
	tableData: any[];
	tableColumns: GridColDef[];
	type: ReportType;
};

type FileType = "EXCEL" | "PDF";

// ─── Year Selection Modal ────────────────────────────────────────────────────
type YearSelectionModalProps = {
	open: boolean;
	onClose: () => void;
	onConfirm: (year: string) => void;
	employeeName: string;
};

const YearSelectionModal = ({
	open,
	onClose,
	onConfirm,
	employeeName,
}: YearSelectionModalProps) => {
	const { currentCompany } = useCompanyStore();
	const [availableYears, setAvailableYears] = useState<string[]>([]);
	const [loadingYears, setLoadingYears] = useState(false);
	const [selectedYear, setSelectedYear] = useState<string | null>(null);

	useEffect(() => {
		if (!open || !currentCompany?.id) return;

		const fetchYears = async () => {
			setLoadingYears(true);
			setSelectedYear(null);
			try {
				const response = await API.PayrollAPI.getPayPeriodYears(
					currentCompany.id,
				);
				const years: string[] = (response as any).data || [];
				setAvailableYears(years);
				// Auto-select the most recent year
				if (years.length > 0) {
					setSelectedYear(years[years.length - 1]);
				}
			} catch (error) {
				API.Handlers.onErrorHandler(error, null, null);
			} finally {
				setLoadingYears(false);
			}
		};

		fetchYears();
	}, [open, currentCompany?.id]);

	return (
		<Dialog
			open={open}
			onClose={onClose}
			maxWidth="xs"
			fullWidth
			PaperProps={{ style: { borderRadius: 12 } }}
		>
			<DialogTitle className="flex items-center justify-between">
				<div>
					<h2 className="text-base font-semibold text-gray-900">Select Year</h2>
					{employeeName && (
						<p className="text-sm text-gray-500 font-normal mt-0.5">
							{employeeName}
						</p>
					)}
				</div>
				<button onClick={onClose} className="text-gray-400 hover:text-gray-600">
					<IoClose className="text-xl" />
				</button>
			</DialogTitle>

			<DialogContent>
				{loadingYears ? (
					<p className="text-sm text-gray-500 py-4 text-center">
						Loading years...
					</p>
				) : availableYears.length === 0 ? (
					<p className="text-sm text-gray-500 py-4 text-center">
						No years available
					</p>
				) : (
					<div className="flex flex-col gap-2 py-2">
						{/* Show descending (most recent first) */}
						{[...availableYears].reverse().map((year) => (
							<button
								key={year}
								onClick={() => setSelectedYear(year)}
								className={`w-full text-left px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
									selectedYear === year
										? "bg-primary-mango text-white"
										: "bg-gray-50 text-gray-700 hover:bg-gray-100"
								}`}
							>
								{year.slice(0, 4)}
							</button>
						))}
					</div>
				)}

				<div className="flex justify-end gap-3 mt-4">
					<button
						onClick={onClose}
						className="px-5 py-2.5 rounded-lg text-sm text-gray-600 border border-gray-300 hover:bg-gray-50"
					>
						Cancel
					</button>
					<button
						onClick={() => selectedYear && onConfirm(selectedYear)}
						disabled={!selectedYear || loadingYears}
						className="px-5 py-2.5 rounded-lg text-sm text-white bg-primary-mango disabled:opacity-50 disabled:cursor-not-allowed"
					>
						View Report
					</button>
				</div>
			</DialogContent>
		</Dialog>
	);
};

// ─── Main Component ───────────────────────────────────────────────────────────
const CompanyPayrollReports = ({
	defaultPayPeriod,
}: {
	defaultPayPeriod: any;
}) => {
	const { currentCompany } = useCompanyStore();
	const { checkUserPermissionsAnd } = useCheckUserPermissions();
	const [payPeriodAnchorEl, setPayPeriodAnchorEl] =
		useState<HTMLButtonElement | null>(null);
	const [reportTableAnchorEl, setReportTableAnchorEl] =
		useState<HTMLButtonElement | null>(null);
	const [pensionTiersAnchorEl, setPensionTiersAnchorEl] =
		useState<HTMLButtonElement | null>(null);
	const [employeeAnchorEl, setEmployeeAnchorEl] =
		useState<HTMLButtonElement | null>(null);
	const [downloadBtnAnchorEl, setDownloadBtnAnchorEl] =
		useState<HTMLButtonElement | null>(null);
	const [activePayPeriod, setActivePayPeriod] =
		useState<PayPeriodDto>(defaultPayPeriod);
	const [activePensionTier, setActivePensionTier] = useState<PensionTier>();
	const [pensionTiers, setPensionTiers] = useState<PensionTier[]>([]);
	const [loadingPensionTiers, setLoadingPensionTiers] = useState(false);
	const downloadBtnRef = useRef<HTMLButtonElement>(null);
	const downloadDropdownRef = useRef<HTMLButtonElement>(null);
	const [downloadFileType, setDownloadFileType] = useState<FileType>("PDF");
	const [downloadingExcelFile, setDownloadingExcelFile] = useState(false);
	const [downloadingPDFFile, setDownloadingPDFFile] = useState(false);

	// ── Employee Annual Tax Deduction state ──────────────────────────────────
	const [activeEmployee, setActiveEmployee] = useState<EmployeeDto | null>(
		null,
	);
	const [activeYear, setActiveYear] = useState<string | null>(null);
	const [yearModalOpen, setYearModalOpen] = useState(false);
	const [pendingEmployee, setPendingEmployee] = useState<EmployeeDto | null>(
		null,
	);
	const [annualTaxData, setAnnualTaxData] = useState<any[]>([]);
	const [annualTaxLoading, setAnnualTaxLoading] = useState(false);

	useEffect(() => {
		if (defaultPayPeriod?.companyId === currentCompany?.id) {
			setActivePayPeriod(defaultPayPeriod);
		} else {
			setActivePayPeriod(undefined as any);
		}
	}, [currentCompany, defaultPayPeriod]);

	// The selected pay period, unless it belongs to a previously selected
	// company (state lags a render behind a company switch)
	const payPeriod: PayPeriodDto | undefined =
		activePayPeriod?.companyId === currentCompany?.id
			? activePayPeriod
			: undefined;

	// Get pension tiers
	useAsyncEffect(
		useLockFn(async () => {
			setLoadingPensionTiers(true);

			try {
				const data = await API.ProcessingCompanyAPI.getPension({
					countryId: currentCompany!.countryId as number,
					limit: APP_CONFIG.PAGE_LIMIT,
					orderBy: "createdAt:desc" as any,
				});

				const pensiontTiers = data.data.reduce(
					(pensionTiers, pensionTier) => [
						...pensionTiers,
						...(pensionTier.tiers || []),
					],
					[] as PensionTier[],
				);
				setPensionTiers(pensiontTiers);
			} catch (error) {
				API.Handlers.onErrorHandler(error, null, null);
			} finally {
				setLoadingPensionTiers(false);
			}
		}),
		[],
	);

	const { data: payPeriods, isLoading: loadingPayPeriods } = useGetPayPeriod({
		companyId: currentCompany?.id,
		limit: APP_CONFIG.PAGE_LIMIT,
		orderBy: "createdAt:desc" as any,
	});

	// Employees list for Annual Tax Deduction dropdown
	const { data: employeesData, isLoading: loadingEmployees } = useGetEmployee({
		companyId: currentCompany?.id,
		limit: APP_CONFIG.PAGE_LIMIT,
		status: "ACTIVE",
		orderBy: EmployeeOrderBy.FIRST_NAME_ASC,
	});
	const employees = employeesData?.data || [];

	const { tableData: ssfReportData, isLoading: ssfReportLoading } =
		useManageTableWithSearchParams<SSFResponseDto>({
			fetchHook: (options) =>
				useGetSSFReport(
					currentCompany?.id!,
					activePensionTier?.name || "",
					options,
				),
			options: {
				companyId: currentCompany?.id,
				payPeriodId: payPeriod?.id,
				pensionTiers: activePensionTier?.name || "",
				orderBy: undefined,
				limit: undefined,
			},
			reloadDeps: [payPeriod, activePensionTier],
		});

	const { tableData: salaryReportData, isLoading: salaryReportLoading } =
		useManageTableWithSearchParams<SalaryReportResponseDto>({
			fetchHook: (options: any) =>
				useGetSalaryReport(currentCompany?.id as number, options),
			options: {
				payPeriodId: payPeriod?.id,
				orderBy: undefined,
				limit: undefined,
			},
			reloadDeps: [payPeriod],
		});

	const {
		tableData: savingSchemeReportData,
		isLoading: savingSchemeReportLoading,
	} = useManageTableWithSearchParams<SavingSchemeReportResponseDto>({
		fetchHook: (options: any) =>
			useGetSavingSchemeReport(currentCompany?.id as number, options),
		options: {
			payPeriodId: payPeriod?.id,
			orderBy: undefined,
			limit: undefined,
		},
		reloadDeps: [payPeriod],
	});

	const { tableData: bankReportData, isLoading: bankReportLoading } =
		useManageTableWithSearchParams<BankReportResponseDto>({
			fetchHook: (options) =>
				useGetBankReport(
					currentCompany?.id as number,
					payPeriod?.id as number,
					options,
				),
			options: { orderBy: undefined, limit: undefined },
			reloadDeps: [payPeriod],
		});

	const { tableData: taxReportData, isLoading: taxReportLoading } =
		useManageTableWithSearchParams<TaxReportResponseDto>({
			fetchHook: (options: any) =>
				useGetTaxReport(currentCompany?.id as number, options),
			options: {
				payPeriodId: payPeriod?.id,
				orderBy: undefined,
				limit: undefined,
			},
			reloadDeps: [payPeriod],
		});

	const { tableData: payrollSummaryData, isLoading: payrollSummaryLoading } =
		useManageTableWithSearchParams<PayrollSummaryResponseDto>({
			fetchHook: (options) =>
				useGetPayrollSummary(currentCompany?.id!, options),
			options: {
				payPeriodId: payPeriod?.id,
			},
			reloadDeps: [payPeriod],
		});

	const {
		tableData: salaryAdvanceReportData,
		isLoading: salaryAdvanceReportLoading,
	} = useManageTableWithSearchParams<SalaryAdvanceReportResponseDto>({
		fetchHook: (options) =>
			useGetSalaryAdvanceReport(currentCompany?.id!, {
				payPeriodId: payPeriod?.id,
				...options,
			}),
		options: {
			payPeriodId: payPeriod?.id,
		},
		reloadDeps: [payPeriod],
	});

	const { tableData: payeSummaryRawData, isLoading: payeSummaryLoading } =
		useManageTableWithSearchParams({
			fetchHook: (options: any) =>
				useGetPayeSummaryReport(currentCompany?.id as number, options),
			options: {
				payPeriodId: payPeriod?.id,
				orderBy: undefined,
				limit: undefined,
			},
			reloadDeps: [payPeriod],
		});

	// Extract categories array from the PAYE Summary response
	// Response shape: { data: { categories: [...], totalStaff, totalEmolument, totalTaxDeducted, ... } }
	const payeSummaryData = useMemo(() => {
		const raw = payeSummaryRawData as any;
		if (!raw) return [];
		if (raw?.data?.categories) return raw.data.categories;
		if (raw?.categories) return raw.categories;
		if (Array.isArray(raw)) return raw;
		return [];
	}, [payeSummaryRawData]);

	// ── Fetch Annual Tax Deduction data when employee + year are both confirmed ──
	useEffect(() => {
		if (!activeEmployee || !activeYear || !currentCompany?.id) {
			setAnnualTaxData([]);
			return;
		}

		const fetchAnnualTax = async () => {
			setAnnualTaxLoading(true);
			setAnnualTaxData([]);
			try {
				const response = await API.PayrollAPI.getEmployeeAnnualTaxDeduction(
					currentCompany.id,
					activeEmployee.id,
					{ year: activeYear },
				);
				const raw = (response as any)
					.data as EmployeeAnnualTaxDeductionResponseDto;
				setAnnualTaxData(raw?.monthlyRows || []);
			} catch (error) {
				API.Handlers.onErrorHandler(error, null, null);
			} finally {
				setAnnualTaxLoading(false);
			}
		};

		fetchAnnualTax();
	}, [activeEmployee, activeYear, currentCompany?.id]);

	// When user picks an employee, open the year modal before fetching anything
	const handleSelectEmployee = (employee: EmployeeDto) => {
		setPendingEmployee(employee);
		setEmployeeAnchorEl(null);
		setYearModalOpen(true);
	};

	// Only set active employee + year (triggering the data fetch) once year is confirmed
	const handleYearConfirmed = (year: string) => {
		setActiveEmployee(pendingEmployee);
		setActiveYear(year);
		setYearModalOpen(false);
		setPendingEmployee(null);
	};

	const salaryAdvanceData = Array.isArray(salaryAdvanceReportData)
		? salaryAdvanceReportData[0]
		: salaryAdvanceReportData;

	const reportTables: ReportTable[] = useMemo(() => {
		const summaryItems = (payrollSummaryData as any)?.summaryItems || [];

		const flattenedSalaryAdvanceData = salaryAdvanceData?.advancesByStation
			? Object.entries(salaryAdvanceData.advancesByStation).flatMap(
					([stationName, advances]) => advances,
				)
			: [];

		return [
			{
				title: "Salary Report",
				tableData: salaryReportData,
				tableColumns: SalaryReportColumns,
				type: "" as any,
			},
			{
				title: "SSF Reports",
				tableData: ssfReportData,
				tableColumns: SSFReportColumns,
				type: ReportType.SSF,
			},
			{
				title: "Saving Scheme Report",
				tableData: savingSchemeReportData,
				tableColumns: SavingSchemeColumns,
				type: ReportType.SAVING_SCHEME,
			},
			{
				title: "Bank Report",
				tableData: bankReportData,
				tableColumns: BankReportColumns,
				type: "BANK_REPORT" as any,
			},
			{
				title: "Tax Report",
				tableData: taxReportData,
				tableColumns: TaxReportColumns,
				type: ReportType.TAX_REPORT,
			},
			{
				title: "Payroll Summary",
				tableData: summaryItems,
				tableColumns: PayrollSummaryColumns,
				type: ReportType.PAYROLL_SUMMARY,
			},
			{
				title: "Salary Advance Report",
				tableData: flattenedSalaryAdvanceData,
				tableColumns: SalaryAdvanceReportColumns,
				type: ReportType.SALARY_ADVANCE,
			},
			{
				title: "PAYE Summary",
				tableData: payeSummaryData,
				tableColumns: [],
				type: ReportType.PAYE_SUMMARY,
			},
			{
				title: "Employee Annual Tax Deductions",
				tableData: annualTaxData,
				tableColumns: [],
				type: ReportType.EMPLOYEE_ANNUAL_TAX_DEDUCTION,
			},
		];
	}, [
		salaryReportData.length,
		ssfReportData.length,
		savingSchemeReportData.length,
		bankReportData.length,
		taxReportData.length,
		activePensionTier,
		payrollSummaryData,
		salaryAdvanceReportData,
		salaryAdvanceData,
		payeSummaryData,
		annualTaxData,
	]);

	const [activeReportTable, setActiveReportTable] = useState(reportTables[0]);
	const [currentReportInfo, setCurrentReportInfo] = useState(reportTables[0]);
	const [validTableData, setValidTableData] = useState(false);

	useEffect(() => {
		const table = reportTables.find(
			(report) => report.title === activeReportTable.title,
		)!;

		if (activeReportTable.title === "SSF Reports") {
			const updatedTable = { ...table, tableData: ssfReportData };
			setCurrentReportInfo(updatedTable);
			setValidTableData(ssfReportData.length > 0);
		} else if (activeReportTable.title === "Employee Annual Tax Deductions") {
			const updatedTable = { ...table, tableData: annualTaxData };
			setCurrentReportInfo(updatedTable);
			setValidTableData(annualTaxData.length > 0);
		} else {
			setCurrentReportInfo(table);
			setValidTableData(table.tableData.length > 0);
		}
	}, [
		activeReportTable,
		reportTables,
		activePensionTier,
		ssfReportData,
		annualTaxData,
	]);

	const handleReportTableChange = (reportTable: ReportTable) => {
		setActiveReportTable(reportTable);
		if (
			reportTable.title === "SSF Reports" &&
			pensionTiers.length &&
			!activePensionTier
		) {
			setActivePensionTier(pensionTiers[0]);
		}
		setReportTableAnchorEl(null);
	};

	const isAnnualTaxReport =
		activeReportTable.title === "Employee Annual Tax Deductions";

	const handleDownloadExcelFile = async () => {
		if (!validTableData) return;
		setDownloadingExcelFile(true);

		// No Excel endpoint for these report types
		if (
			currentReportInfo.type === ReportType.PAYE_SUMMARY ||
			currentReportInfo.type === ReportType.EMPLOYEE_ANNUAL_TAX_DEDUCTION
		) {
			setDownloadingExcelFile(false);
			return;
		}

		if (currentReportInfo.type === ReportType.PAYROLL_SUMMARY) {
			try {
				const response = await API.PayrollAPI.getPayrollSummaryExcel(
					currentCompany!.id,
					payPeriod!.id,
					{},
				);

				if (response && response.data) {
					const link = document.createElement("a");
					link.href = response.data;
					link.download = `${
						payPeriod?.code
					}_Payroll_Summary.xlsx`;
					document.body.appendChild(link);
					link.click();
					document.body.removeChild(link);
				} else {
					console.error(
						"No download URL returned from Payroll Summary Excel API",
					);
				}
			} catch (error) {
				console.error("Error downloading Payroll Summary Excel:", error);
				API.Handlers.onErrorHandler(error, null, null);
			} finally {
				setDownloadingExcelFile(false);
			}
			return;
		}

		if (currentReportInfo.type === ReportType.SALARY_ADVANCE) {
			try {
				const response = await API.PayrollAPI.getSalaryAdvanceReportExcel(
					currentCompany!.id,
					payPeriod!.id,
					{},
				);

				if (response && response.data) {
					const link = document.createElement("a");
					link.href = response?.data;
					link.download = `${
						payPeriod?.code
					}_Salary_Advance_Report.xlsx`;
					document.body.appendChild(link);
					link.click();
					document.body.removeChild(link);
				} else {
					console.error(
						"No download URL returned from Salary Advance Excel API",
					);
				}
			} catch (error) {
				console.error("Error downloading Salary Advance Excel:", error);
				API.Handlers.onErrorHandler(error, null, null);
			} finally {
				setDownloadingExcelFile(false);
			}
			return;
		}

		if (currentReportInfo.type === ReportType.SSF) {
			const response = await API.PayrollAPI.getSSFReportExcel(
				currentCompany!.id,
				payPeriod!.id,
				activePensionTier?.name || "",
				{},
			);

			if (response && response.data) {
				const link = document.createElement("a");
				link.href = response.data;
				link.download = `${
					payPeriod?.code
				}_SSF_Report.xlsx`;
				document.body.appendChild(link);
				link.click();
				document.body.removeChild(link);
				setDownloadingExcelFile(false);
				return;
			}
		}

		if (currentReportInfo.type === ReportType.SAVING_SCHEME) {
			try {
				const response = await API.PayrollAPI.getSavingSchemeReportExcel(
					currentCompany!.id,
					payPeriod!.id,
					{},
				);

				if (response && response.data) {
					const link = document.createElement("a");
					link.href = response.data;
					link.download = `${
						payPeriod?.code
					}_Saving_Scheme_Report.xlsx`;
					document.body.appendChild(link);
					link.click();
					document.body.removeChild(link);
				} else {
					console.error(
						"No download URL returned from Saving Scheme Excel API",
					);
				}
			} catch (error) {
				console.error("Error downloading Saving Scheme Excel:", error);
				API.Handlers.onErrorHandler(error, null, null);
			} finally {
				setDownloadingExcelFile(false);
			}
			return;
		}

		if (currentReportInfo.type === ReportType.TAX_REPORT) {
			try {
				const response = await API.PayrollAPI.getTaxReportExcel(
					currentCompany!.id,
					payPeriod!.id,
					{},
				);

				if (response && response.data) {
					const link = document.createElement("a");
					link.href = response.data;
					link.download = `${
						payPeriod?.code
					}_Tax_Report.xlsx`;
					document.body.appendChild(link);
					link.click();
					document.body.removeChild(link);
				} else {
					console.error("No download URL returned from Tax Report Excel API");
				}
			} catch (error) {
				console.error("Error downloading Tax Report Excel:", error);
				API.Handlers.onErrorHandler(error, null, null);
			} finally {
				setDownloadingExcelFile(false);
			}
			return;
		}

		if (currentReportInfo.type === ReportType.BANK_REPORT) {
			try {
				const response = await API.PayrollAPI.getBankReportExcel(
					currentCompany!.id,
					payPeriod!.id,
					{},
				);

				if (response && response.data) {
					const link = document.createElement("a");
					link.href = response.data;
					link.download = `${
						payPeriod?.code
					}_Bank_Report.xlsx`;
					document.body.appendChild(link);
					link.click();
					document.body.removeChild(link);
				} else {
					console.error("No download URL returned from Bank Report Excel API");
				}
			} catch (error) {
				console.error("Error downloading Bank Report Excel:", error);
				API.Handlers.onErrorHandler(error, null, null);
			} finally {
				setDownloadingExcelFile(false);
			}
			return;
		}

		try {
			const response = await API.PayrollAPI.getSalaryReportExcel(
				currentCompany!.id,
				payPeriod!.id,
				{},
			);

			if (response && response.data) {
				const link = document.createElement("a");
				link.href = response.data;
				link.download = `${
					payPeriod?.code
				}_Salary_Report.xlsx`;
				document.body.appendChild(link);
				link.click();
				document.body.removeChild(link);
			} else {
				console.error("No download URL returned from Salary Report Excel API");
			}
		} catch (error) {
			console.error("Error downloading Salary Report Excel:", error);
			API.Handlers.onErrorHandler(error, null, null);
		} finally {
			setDownloadingExcelFile(false);
		}
	};

	const handleDownloadPDFFile = async () => {
		if (!validTableData) return;
		setDownloadingPDFFile(true);

		if (currentReportInfo.type === ReportType.PAYROLL_SUMMARY) {
			try {
				const response = await API.PayrollAPI.getPayrollSummaryPdf(
					currentCompany!.id,
					payPeriod!.id,
					{},
				);

				if (response && response.data) {
					const link = document.createElement("a");
					link.target = "_blank";
					link.href = response.data;
					link.download = `${
						payPeriod?.code
					}_Payroll_Summary.pdf`;
					document.body.appendChild(link);
					link.click();
					document.body.removeChild(link);
				} else {
					console.error(
						"No download URL returned from Payroll Summary PDF API",
					);
				}
			} catch (error) {
				console.error("Error downloading Payroll Summary PDF:", error);
				API.Handlers.onErrorHandler(error, null, null);
			} finally {
				setDownloadingPDFFile(false);
			}
			return;
		}

		if (currentReportInfo.type === ReportType.SSF) {
			try {
				const response = await API.PayrollAPI.getSSFReportPdf(
					currentCompany!.id,
					payPeriod!.id,
					activePensionTier?.name || "",
					{},
				);

				if (response && response.data) {
					const link = document.createElement("a");
					link.target = "_blank";
					link.href = response.data;
					link.download = `${
						payPeriod?.code
					}_SSF_Summary.pdf`;
					document.body.appendChild(link);
					link.click();
					document.body.removeChild(link);
				} else {
					console.error("No download URL returned from SSF Report PDF API");
				}
			} catch (error) {
				console.error("Error downloading SSF Report PDF:", error);
				API.Handlers.onErrorHandler(error, null, null);
			} finally {
				setDownloadingPDFFile(false);
			}
			return;
		}

		if (currentReportInfo.type === ReportType.SALARY_ADVANCE) {
			try {
				const response = await API.PayrollAPI.getSalaryAdvanceReportPdf(
					currentCompany!.id,
					payPeriod!.id,
					{},
				);

				if (response && response.data) {
					const link = document.createElement("a");
					link.target = "_blank";
					link.href = response.data;
					link.download = `${
						payPeriod?.code
					}_Salary_Advance_Report.pdf`;
					document.body.appendChild(link);
					link.click();
					document.body.removeChild(link);
				} else {
					console.error("No download URL returned from Salary Advance PDF API");
				}
			} catch (error) {
				console.error("Error downloading Salary Advance PDF:", error);
				API.Handlers.onErrorHandler(error, null, null);
			} finally {
				setDownloadingPDFFile(false);
			}
			return;
		}

		if (currentReportInfo.type === ReportType.SAVING_SCHEME) {
			try {
				const response = await API.PayrollAPI.getSavingSchemeReportPdf(
					currentCompany!.id,
					payPeriod!.id,
					{},
				);

				if (response && response.data) {
					const link = document.createElement("a");
					link.target = "_blank";
					link.href = response.data;
					link.download = `${
						payPeriod?.code
					}_Saving_Scheme_Report.pdf`;
					document.body.appendChild(link);
					link.click();
					document.body.removeChild(link);
				} else {
					console.error("No download URL returned from Saving Scheme PDF API");
				}
			} catch (error) {
				console.error("Error downloading Saving Scheme PDF:", error);
				API.Handlers.onErrorHandler(error, null, null);
			} finally {
				setDownloadingPDFFile(false);
			}
			return;
		}

		if (currentReportInfo.type === ReportType.TAX_REPORT) {
			try {
				const response = await API.PayrollAPI.getTaxReportPdf(
					currentCompany!.id,
					payPeriod!.id,
					{},
				);

				if (response && response.data) {
					const link = document.createElement("a");
					link.target = "_blank";
					link.href = response.data;
					link.download = `${
						payPeriod?.code
					}_Tax_Report.pdf`;
					document.body.appendChild(link);
					link.click();
					document.body.removeChild(link);
				} else {
					console.error("No download URL returned from Tax Report PDF API");
				}
			} catch (error) {
				console.error("Error downloading Tax Report PDF:", error);
				API.Handlers.onErrorHandler(error, null, null);
			} finally {
				setDownloadingPDFFile(false);
			}
			return;
		}

		if (currentReportInfo.type === ReportType.BANK_REPORT) {
			try {
				const response = await API.PayrollAPI.getBankReportPdf(
					currentCompany!.id,
					payPeriod!.id,
					{},
				);

				if (response && response.data) {
					const link = document.createElement("a");
					link.target = "_blank";
					link.href = response.data;
					link.download = `${
						payPeriod?.code
					}_Bank_Report.pdf`;
					document.body.appendChild(link);
					link.click();
					document.body.removeChild(link);
				} else {
					console.error("No download URL returned from Bank Report PDF API");
				}
			} catch (error) {
				console.error("Error downloading Bank Report PDF:", error);
				API.Handlers.onErrorHandler(error, null, null);
			} finally {
				setDownloadingPDFFile(false);
			}
			return;
		}

		if (currentReportInfo.type === ReportType.SALARY_REPORT) {
			try {
				const response = await API.PayrollAPI.getSalaryReportPdf(
					currentCompany!.id,
					payPeriod!.id,
					{},
				);

				if (response && response.data) {
					const link = document.createElement("a");
					link.target = "_blank";
					link.href = response.data;
					link.download = `${
						payPeriod?.code
					}_Salary_Report.pdf`;
					document.body.appendChild(link);
					link.click();
					document.body.removeChild(link);
				} else {
					console.error("No download URL returned from Salary Report PDF API");
				}
			} catch (error) {
				console.error("Error downloading Salary Report PDF:", error);
				API.Handlers.onErrorHandler(error, null, null);
			} finally {
				setDownloadingPDFFile(false);
			}
			return;
		}

		if (currentReportInfo.type === ReportType.PAYE_SUMMARY) {
			try {
				const response = await API.PayrollAPI.getPayeSummaryPdf(
					currentCompany!.id,
					payPeriod!.id,
					{},
				);

				if (response && response.data) {
					const link = document.createElement("a");
					link.target = "_blank";
					link.href = response.data;
					link.download = `${
						payPeriod?.code
					}_PAYE_Summary.pdf`;
					document.body.appendChild(link);
					link.click();
					document.body.removeChild(link);
				} else {
					console.error("No download URL returned from PAYE Summary PDF API");
				}
			} catch (error) {
				console.error("Error downloading PAYE Summary PDF:", error);
				API.Handlers.onErrorHandler(error, null, null);
			} finally {
				setDownloadingPDFFile(false);
			}
			return;
		}

		if (currentReportInfo.type === ReportType.EMPLOYEE_ANNUAL_TAX_DEDUCTION) {
			if (!activeEmployee || !activeYear) {
				setDownloadingPDFFile(false);
				return;
			}
			try {
				const response = await API.PayrollAPI.getEmployeeAnnualTaxDeductionPdf(
					currentCompany!.id,
					activeEmployee.id,
					{ year: activeYear },
				);

				if (response && response.data) {
					const link = document.createElement("a");
					link.target = "_blank";
					link.href = response.data;
					link.download = `${activeEmployee.firstName}_${activeEmployee.lastName}_Annual_Tax_${activeYear}.pdf`;
					document.body.appendChild(link);
					link.click();
					document.body.removeChild(link);
				} else {
					console.error(
						"No download URL returned from Annual Tax Deduction PDF API",
					);
				}
			} catch (error) {
				console.error("Error downloading Annual Tax Deduction PDF:", error);
				API.Handlers.onErrorHandler(error, null, null);
			} finally {
				setDownloadingPDFFile(false);
			}
			return;
		}

		try {
			const pdf = new jsPDF("l", "mm", "a4");
			// Calculate how many pages we need based on 25 items per page
			const totalPages = Math.ceil(
				(currentReportInfo.tableData?.length || 0) / 25,
			);

			for (let pageIndex = 0; pageIndex < totalPages; pageIndex++) {
				// Get the HTML element for current page
				const input = document.getElementById(`pdf-content-page-${pageIndex}`);
				if (!input) continue;

				// Add a new page to PDF for all pages after first one
				if (pageIndex > 0) {
					pdf.addPage();
				}

				// Convert HTML content to canvas, then to image
				const canvas = await html2canvas(input);
				const imgData = canvas.toDataURL("image/png");

				// Get PDF page dimensions
				const pdfWidth = pdf.internal.pageSize.getWidth();
				const pdfHeight = pdf.internal.pageSize.getHeight();

				// Get original image dimensions
				const imgWidth = canvas.width;
				const imgHeight = canvas.height;
				const aspectRatio = imgWidth / imgHeight;

				// Calculate new image dimensions while maintaining aspect ratio
				let finalImgWidth = pdfWidth;
				let finalImgHeight = pdfWidth / aspectRatio;

				// If height exceeds PDF height, recalculate dimensions using full PDF height
				if (finalImgHeight > pdfHeight) {
					finalImgHeight = pdfHeight;
					finalImgWidth = pdfHeight * aspectRatio;
				}

				// Center the image on the PDF page
				const x = (pdfWidth - finalImgWidth) / 2;
				const y = (pdfHeight - finalImgHeight) / 2;

				// Add the image to PDF with "FAST" optimization for better performance
				pdf.addImage(
					imgData,
					"PNG",
					x,
					y,
					finalImgWidth,
					finalImgHeight,
					"",
					"FAST",
				);
			}

			const reportName =
				activeReportTable.title === "SSF Reports"
					? activePensionTier?.name + " Report"
					: activeReportTable.title;

			pdf.save(
				`${payPeriod?.code}_${reportName}.pdf`,
			);
		} catch (error) {
			console.error("Error generating PDF:", error);
		} finally {
			setDownloadingPDFFile(false);
		}
	};

	return (
		<>
			<div className="flex justify-between items-center mt-4 mb-8">
				<div className="flex items-center gap-3">
					{/* Pay Period dropdown — hidden for Annual Tax report (uses year instead) */}
					{!isAnnualTaxReport && (
						<>
							<button
								onClick={(e: any) => setPayPeriodAnchorEl(e.currentTarget)}
								className="relative border border-gray-300 rounded-lg text-2xs py-3 px-3 flex items-center gap-3"
							>
								<div className="absolute top-0 -translate-y-1/2 left-2 px-2 bg-white text-[11px]">
									Pay Period
								</div>
								{payPeriod?.id ? (
									<span className="max-w-full truncate">
										{payPeriod.code + " "}
										<span className="text-xs">
											({formatDate(payPeriod.startDate)} -{" "}
											{formatDate(payPeriod.endDate)})
										</span>
									</span>
								) : (
									<span className="pr-5">Select Option</span>
								)}
								<IoCaretDown />
							</button>
							<Popover
								open={Boolean(payPeriodAnchorEl)}
								anchorEl={payPeriodAnchorEl}
								onClose={() => setPayPeriodAnchorEl(null)}
								anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
								transformOrigin={{ vertical: "top", horizontal: "left" }}
								PaperProps={{ style: { ...DropdownStyle } }}
							>
								<div className="py-1 flex flex-col">
									{loadingPayPeriods ? <p>Loading...</p> : null}
									{!payPeriods?.data.length && !loadingPayPeriods ? (
										<p>No Pay Periods found</p>
									) : null}
									{payPeriods?.data.map((payPeriod) => (
										<button
											key={payPeriod.id}
											className={`w-full pl-4 pr-7 py-2 text-start text-gray-700 ${
												activePayPeriod?.id === payPeriod.id
													? "bg-gray-200"
													: "hover:bg-gray-100"
											}`}
											role="menuitem"
											onClick={() => {
												setActivePayPeriod(payPeriod);
												setPayPeriodAnchorEl(null);
											}}
										>
											{payPeriod.code + " "}
											<span className="text-xs">
												({formatDate(payPeriod.startDate)} -{" "}
												{formatDate(payPeriod.endDate)})
											</span>
										</button>
									))}
								</div>
							</Popover>
						</>
					)}

					{/* Report Type dropdown */}
					<>
						<button
							onClick={(e: any) => setReportTableAnchorEl(e.currentTarget)}
							className="relative border border-gray-300 rounded-lg text-2xs py-3 px-3 flex items-center gap-3"
						>
							<div className="absolute top-0 -translate-y-1/2 left-2 px-2 bg-white text-[11px]">
								Report Type
							</div>
							<span className="pr-5">{activeReportTable.title}</span>
							<IoCaretDown />
						</button>
						<Popover
							open={Boolean(reportTableAnchorEl)}
							anchorEl={reportTableAnchorEl}
							onClose={() => setReportTableAnchorEl(null)}
							anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
							transformOrigin={{ vertical: "top", horizontal: "left" }}
							PaperProps={{ style: { ...DropdownStyle } }}
						>
							<div className="py-1 flex flex-col">
								{reportTables.map((reportTable) => (
									<button
										key={reportTable.title}
										className={`w-full pl-4 pr-7 py-2 text-start text-gray-700 ${
											activeReportTable.title === reportTable.title
												? "bg-gray-200"
												: "hover:bg-gray-100"
										}`}
										role="menuitem"
										onClick={() => handleReportTableChange(reportTable)}
									>
										{reportTable.title}
									</button>
								))}
							</div>
						</Popover>
					</>

					{/* SSF Type dropdown */}
					{activeReportTable.title === "SSF Reports" && (
						<>
							<button
								onClick={(e: any) => setPensionTiersAnchorEl(e.currentTarget)}
								className="relative border border-gray-300 rounded-lg text-2xs py-3 px-3 flex items-center gap-3"
							>
								<div className="absolute top-0 -translate-y-1/2 left-2 px-2 bg-white text-[11px]">
									SSF Type
								</div>
								{activePensionTier ? (
									<span className="pr-5">{activePensionTier.name}</span>
								) : (
									<span className="pr-5">Select Name</span>
								)}
								<IoCaretDown />
							</button>
							<Popover
								open={Boolean(pensionTiersAnchorEl)}
								anchorEl={pensionTiersAnchorEl}
								onClose={() => setPensionTiersAnchorEl(null)}
								anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
								transformOrigin={{ vertical: "top", horizontal: "left" }}
								PaperProps={{ style: { ...DropdownStyle } }}
							>
								<div className="py-1 flex flex-col">
									{loadingPensionTiers ? <p>Loading...</p> : null}
									{!pensionTiers.length && !loadingPensionTiers ? (
										<p>No pension tiers found</p>
									) : null}
									{pensionTiers.map((tier) => (
										<button
											key={tier.id}
											className={`w-full pl-4 pr-7 py-2 text-start text-gray-700 ${
												activePensionTier?.id === tier.id
													? "bg-gray-200"
													: "hover:bg-gray-100"
											}`}
											role="menuitem"
											onClick={() => {
												setActivePensionTier(tier);
												setPensionTiersAnchorEl(null);
											}}
										>
											{tier.name}
										</button>
									))}
								</div>
							</Popover>
						</>
					)}

					{/* Employee dropdown — only for Annual Tax Deductions */}
					{isAnnualTaxReport && (
						<>
							<button
								onClick={(e: any) => setEmployeeAnchorEl(e.currentTarget)}
								className="relative border border-gray-300 rounded-lg text-2xs py-3 px-3 flex items-center gap-3"
							>
								<div className="absolute top-0 -translate-y-1/2 left-2 px-2 bg-white text-[11px]">
									Employee
								</div>
								{activeEmployee ? (
									<span className="pr-5">
										{activeEmployee.firstName} {activeEmployee.lastName}
									</span>
								) : (
									<span className="pr-5">Select Employee</span>
								)}
								<IoCaretDown />
							</button>
							<Popover
								open={Boolean(employeeAnchorEl)}
								anchorEl={employeeAnchorEl}
								onClose={() => setEmployeeAnchorEl(null)}
								anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
								transformOrigin={{ vertical: "top", horizontal: "left" }}
								PaperProps={{
									style: {
										...DropdownStyle,
										maxHeight: 300,
										overflowY: "auto",
									},
								}}
							>
								<div className="py-1 flex flex-col">
									{loadingEmployees ? (
										<p className="px-4 py-2 text-sm text-gray-500">
											Loading...
										</p>
									) : null}
									{!employees.length && !loadingEmployees ? (
										<p className="px-4 py-2 text-sm text-gray-500">
											No employees found
										</p>
									) : null}
									{employees.map((employee) => (
										<button
											key={employee.id}
											className={`w-full pl-4 pr-7 py-2 text-start text-gray-700 ${
												activeEmployee?.id === employee.id
													? "bg-gray-200"
													: "hover:bg-gray-100"
											}`}
											role="menuitem"
											onClick={() => handleSelectEmployee(employee)}
										>
											{employee.firstName} {employee.lastName}
										</button>
									))}
								</div>
							</Popover>

							{/* Year button — appears after employee + year are confirmed, allows changing year */}
							{activeEmployee && activeYear && (
								<button
									onClick={() => {
										setPendingEmployee(activeEmployee);
										setYearModalOpen(true);
									}}
									className="relative border border-gray-300 rounded-lg text-2xs py-3 px-3 flex items-center gap-3"
								>
									<div className="absolute top-0 -translate-y-1/2 left-2 px-2 bg-white text-[11px]">
										Year
									</div>
									<span className="pr-5">{activeYear.slice(0, 4)}</span>
									<IoCaretDown />
								</button>
							)}
						</>
					)}
				</div>

				{/* Download button */}
				<div className="flex items-center gap-1">
					<button
						ref={downloadBtnRef}
						onClick={() => {
							downloadFileType === "PDF"
								? handleDownloadPDFFile()
								: handleDownloadExcelFile();
						}}
						className="w-fit h-[56px] bg-primary-mango pl-6 pr-8 rounded-l-lg text-white text-sm font-medium flex items-center gap-3"
						disabled={
							!validTableData || downloadingPDFFile || downloadingExcelFile
						}
					>
						{validTableData && <BsDownload className="text-xl" />}
						<span>
							{!validTableData
								? isAnnualTaxReport && !activeEmployee
									? "Select an Employee"
									: "No Data Available"
								: downloadingPDFFile
									? "Downloading..."
									: downloadFileType === "PDF"
										? "Download PDF File"
										: "Download Excel File"}
						</span>
					</button>
					<>
						<CustomizedTooltip title="Select file type" placement="top" arrow>
							<Typography>
								<button
									ref={downloadDropdownRef}
									onClick={(e: any) => setDownloadBtnAnchorEl(e.currentTarget)}
									className="w-fit h-[56px] bg-primary-mango px-3.5 rounded-r-lg text-white text-xl leading-[20px] flex items-center"
									disabled={
										!validTableData ||
										downloadingPDFFile ||
										downloadingExcelFile
									}
								>
									<FaSortDown className="mb-1" />
								</button>
							</Typography>
						</CustomizedTooltip>
						<Popover
							open={Boolean(downloadBtnAnchorEl)}
							anchorEl={downloadBtnAnchorEl}
							onClose={() => setDownloadBtnAnchorEl(null)}
							anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
							transformOrigin={{ vertical: "top", horizontal: "right" }}
							PaperProps={{
								style: {
									width: `${
										(downloadBtnRef.current?.offsetWidth || 0) +
										(downloadDropdownRef.current?.offsetWidth || 0) +
										4
									}px`,
									height: "auto",
									fontSize: "15px",
									color: "#4F4F4F",
									borderRadius: 10,
									background: "white",
									boxShadow: "-10px -5px 30px 0px rgba(0, 0, 0, 0.15)",
								},
							}}
						>
							<div className="w-full py-1 flex flex-col">
								{/* Hide Excel option for PDF-only reports */}
								{currentReportInfo.type !== ReportType.PAYE_SUMMARY &&
									currentReportInfo.type !==
										ReportType.EMPLOYEE_ANNUAL_TAX_DEDUCTION && (
										<button
											onClick={() => {
												setDownloadFileType("EXCEL");
												setDownloadBtnAnchorEl(null);
											}}
											className={`w-full py-2 px-5 text-start ${
												downloadFileType === "EXCEL"
													? "bg-gray-200"
													: "hover:bg-gray-100"
											}`}
										>
											Download as Excel File
										</button>
									)}
								<button
									onClick={() => {
										setDownloadFileType("PDF");
										setDownloadBtnAnchorEl(null);
									}}
									className={`w-full py-2 px-5 text-start ${
										downloadFileType === "PDF"
											? "bg-gray-200"
											: "hover:bg-gray-100"
									}`}
								>
									Download as PDF File
								</button>
							</div>
						</Popover>
					</>
				</div>
			</div>

			{/* Tables */}
			{activeReportTable.title === "Salary Report" ? (
				<TanStackEmployeeTable
					tableId={TableId.SALARY_REPORT}
					data={salaryReportData}
					columns={salaryReportColumns}
					loading={salaryReportLoading}
					hasPermission={checkUserPermissionsAnd([PERMISSIONS.PAYSLIPS_READ])}
				/>
			) : activeReportTable.title === "SSF Reports" ? (
				<TanStackEmployeeTable
					tableId={TableId.SSF_REPORT}
					data={ssfReportData}
					columns={ssfReportColumns}
					loading={ssfReportLoading}
					hasPermission={checkUserPermissionsAnd([PERMISSIONS.PAYSLIPS_READ])}
				/>
			) : activeReportTable.title === "Bank Report" ? (
				<TanStackEmployeeTable
					tableId={TableId.BANK_REPORT}
					data={bankReportData}
					columns={bankReportColumns}
					loading={bankReportLoading}
					hasPermission={checkUserPermissionsAnd([PERMISSIONS.PAYSLIPS_READ])}
				/>
			) : activeReportTable.title === "Payroll Summary" ? (
				<TanStackEmployeeTable
					tableId={TableId.PAYROLL_SUMMARY}
					data={((payrollSummaryData as any)?.summaryItems || []).map(
						(item: any, index: number) => ({
							...item,
							id: index,
						}),
					)}
					columns={payrollSummaryColumns}
					loading={payrollSummaryLoading}
					hasPermission={checkUserPermissionsAnd([PERMISSIONS.PAYSLIPS_READ])}
					pageSize={15}
				/>
			) : activeReportTable.title === "Salary Advance Report" ? (
				<TanStackEmployeeTable
					tableId={TableId.SALARY_ADVANCE_REPORT}
					data={
						salaryAdvanceData?.advancesByStation
							? Object.entries(salaryAdvanceData.advancesByStation).flatMap(
									([stationName, advances], stationIndex) => {
										const rows: any[] = [];
										if (stationName !== "No Station") {
											rows.push({
												id: `station-${stationIndex}`,
												isStationHeader: true,
												stationName,
											});
										}
										rows.push(
											...(
												advances as {
													employeeCode: string;
													employeeName: string;
													advanceAmount: number;
												}[]
											).map((item, index) => ({
												id: `station-${stationIndex}-item-${index}`,
												...item,
											})),
										);
										return rows;
									},
								)
							: []
					}
					columns={salaryAdvanceReportColumns}
					loading={salaryAdvanceReportLoading}
					hasPermission={checkUserPermissionsAnd([PERMISSIONS.PAYSLIPS_READ])}
				/>
			) : activeReportTable.title === "Tax Report" ? (
				<TanStackEmployeeTable
					tableId={TableId.TAX_REPORT}
					data={taxReportData}
					columns={taxReportColumns}
					loading={taxReportLoading}
					hasPermission={checkUserPermissionsAnd([PERMISSIONS.PAYSLIPS_READ])}
				/>
			) : activeReportTable.title === "PAYE Summary" ? (
				<TanStackEmployeeTable
					tableId={TableId.PAYE_SUMMARY_REPORT}
					data={payeSummaryData}
					columns={payeSummaryColumns}
					loading={payeSummaryLoading}
					hasPermission={checkUserPermissionsAnd([PERMISSIONS.PAYSLIPS_READ])}
				/>
			) : activeReportTable.title === "Employee Annual Tax Deductions" ? (
				<TanStackEmployeeTable
					tableId={TableId.EMPLOYEE_ANNUAL_TAX_DEDUCTION}
					data={annualTaxData}
					columns={employeeAnnualTaxDeductionColumns}
					loading={annualTaxLoading}
					hasPermission={checkUserPermissionsAnd([PERMISSIONS.PAYSLIPS_READ])}
				/>
			) : (
				<TanStackEmployeeTable
					tableId={TableId.SAVING_SCHEME_REPORT}
					data={savingSchemeReportData}
					columns={savingSchemeColumns}
					loading={savingSchemeReportLoading}
					hasPermission={checkUserPermissionsAnd([PERMISSIONS.PAYSLIPS_READ])}
				/>
			)}

			<PayrollReportPDF
				reportInfo={currentReportInfo}
				payPeriod={payPeriod!}
				pensionTierName={activePensionTier?.name || ""}
			/>

			{/* Year Selection Modal */}
			<YearSelectionModal
				open={yearModalOpen}
				onClose={() => {
					setYearModalOpen(false);
					setPendingEmployee(null);
				}}
				onConfirm={handleYearConfirmed}
				employeeName={
					pendingEmployee
						? `${pendingEmployee.firstName} ${pendingEmployee.lastName}`
						: ""
				}
			/>
		</>
	);
};

export default CompanyPayrollReports;

const SalaryExcelCols = [
	"Employee Code",
	"Employee Name",
	"Basic Pay",
	"Allowance Amount",
	"Gross Amount",
	"SSF Employee Amount",
	"Taxable Amount",
	"Income Tax Amount",
	"Saving Schemes Amount",
	"Loan Amount",
	"Bonus  Amount",
	"Deductible Relief",
	"Total Relief",
	"Explicit Deductions",
	"Other Deductions Amount",
	"Total Deductions Amount",
	"Primary Pay",
	"Secondary Pay",
	"Net Pay",
];
const SALARY_ORDERED_KEYS = [
	"employeeCode",
	"employeeName",
	"basicPayAmount",
	"allowanceAmount",
	"grossAmount",
	"ssfEmployeeAmount",
	"taxableAmount",
	"incomeTaxAmount",
	"savingSchemesAmount",
	"loansAmount",
	"bonusAmount",
	"deductibleReliefAmount",
	"totalReliefAmount",
	"explicitDeductionAmount",
	"otherDeductionsAmount",
	"totalDeductionsAmount",
	"primaryPayAmount",
	"secondaryPayAmount",
	"netAmount",
];

const SSFExcelCols = [
	"Employee Code",
	"Last Name",
	"First Name",
	"Other Names",
	"Full Name",
	"SSN",
	"Basic Salary",
	"Employee Contribution",
	"Employer Contribution",
	"Contribution Total",
	"Tier Amount",
	"National ID Number",
];
const SSF_ORDERED_KEYS = [
	"id",
	"lastName",
	"firstName",
	"otherNames",
	"fullName",
	"ssn",
	"basicSalary",
	"employeeContribution",
	"employerContribution",
	"total",
	"tierAmount",
	"nationalIdNumber",
];

const SavingSchemeExcelCols = [
	"Employee Code",
	"Surname",
	"Firstname",
	"Date of Birth",
	"Date Joined Company",
	"Social Security Number",
	"Basic Salary",
	"Employee Contribution",
	"Employer Contribution",
	"Mobile Number",
	"Sex",
];
const SavingSchemeOrderedKeys = [
	"id",
	"lastName",
	"firstName",
	"birthDate",
	"employmentDate",
	"ssn",
	"basicSalary",
	"employeeContribution",
	"employerContribution",
	"msisdn",
	"gender",
];

const BankReportExcelCols = [
	"EMPLOYEE CODE",
	"EMPLOYEE NAME",
	"BANK NAME",
	"BANK BRANCH NAME",
	"ACCOUNT NUMBER",
	"BANK CODE",
	"BANK BRANCH CODE",
	"NET",
	"PERIOD DATE",
	"REMARK",
];
const BankReportOrderedKeys = [
	"EMPLOYEE_CODE",
	"EMPLOYEE_NAME",
	"BANK_NAME",
	"BANK_BRANCH_NAME",
	"ACCOUNT_NUMBER",
	"BANK_CODE",
	"BANK_BRANCH_CODE",
	"NET",
	"PERIOD_DATE",
	"REMARKS",
];

const TaxReportExcelCols = [
	"TIN/NATIONAL ID",
	"Employee Name",
	"Position",
	"Residency Type",
	"Basic Salary Amount",
	"Is Secondary Employment",
	"Paid SSNIT",
	"Social Security Amount",
	"Third Tier",
	"Allowance Amount",
	"Bonus Amount",
	"Bonus Tax Amount",
	"Excess Bonus Amount",
	"Total Cash Emolument",
	"Benefit In Kind Amount",
	"Total Assessable Income Amount",
	"Deductible Relief Amount",
	"Total Relief Amount",
	"Chargeable Income Amount",
	"Tax Deductible Amount",
	"Overtime Income Amount",
	"Overtime Tax Amount",
	"Total Tax Payable Amount",
];
const TaxReportOrderedKeys = [
	"employeeTaxNumber",
	"employeeName",
	"position",
	"residencyType",
	"basicSalaryAmount",
	"isSecondaryEmployment",
	"paidSSNIT",
	"socialSecurityAmount",
	"thirdTier",
	"allowanceAmount",
	"bonusAmount",
	"bonusTaxAmount",
	"excessBonusAmount",
	"totalCashEmolument",
	"benefitInKindAmount",
	"totalAssessableIncomeAmount",
	"deductibleReliefAmount",
	"totalReliefAmount",
	"chargeableIncomeAmount",
	"taxDeductibleAmount",
	"overtimeIncomeAmount",
	"overtimeTaxAmount",
	"totalTaxPayableAmount",
];
